import axios from 'axios';
import { Crypter } from '../../../Functions/Security/Crypter';
import type { KycApiResponse } from '../../../Models/DemandeKyc';

// Les routes KYC vivent dans Data.json (Data.ApiUrl.Kyc*), comme partout
// ailleurs dans l'appli — ce fichier ne contient que les deux helpers de
// (dé)chiffrement du corps/de la réponse, propres à la convention d'enveloppe
// utilisée par ces routes ({ Value: { StatutCode, Message, Result } }).

export const encryptBody = (payload: unknown) => Crypter.encrypt(JSON.stringify(payload));

// Même convention que le reste de l'appli : le corps chiffré est renvoyé tel
// quel par axios (response.data est une chaîne), on le déchiffre puis on
// déballe { Value: { StatutCode, Message, Result } } / { StatutCode, ... }.
export function parseEncryptedResponse<T = any>(response: any): KycApiResponse<T> | null {
  if (!response || response.data === undefined || response.data === null) return null;

  try {
    const raw = typeof response.data === 'string' ? response.data : JSON.stringify(response.data);
    const decoded = Crypter.decrypt(raw);
    const parsed = JSON.parse(decoded);
    const unwrapped = parsed?.Value ?? parsed?.value ?? parsed;
    return unwrapped as KycApiResponse<T>;
  } catch {
    return null;
  }
}

// Lecture d'une liste (picklist/relations) via un en-tête chiffré unique —
// même convention partout (GetRelationsClient, GetSignatairesCompte...).
// Partagé par KycPage et les pages dédiées à la modification d'identité
// signataire/procuration, pour éviter de dupliquer ce helper.
export async function fetchList<T>(url: string, header: string, value: string): Promise<T[]> {
  try {
    const response = await axios.get(url, {
      headers: { [header]: Crypter.encrypt(value), 'Content-Type': 'application/json' },
      withCredentials: true,
    });
    const res = JSON.parse(Crypter.decrypt(response.data));
    return res?.StatutCode === 200 ? (res?.Result?.Table ?? []) : [];
  } catch {
    return [];
  }
}

// Recherche directe d'une fiche unique par son code (GetByCodRel/
// GetByRefprocur) — contrairement à fetchList, ici un "non trouvé" (404,
// Result:null) est un résultat légitime à afficher (Message du backend :
// "Relation introuvable : XXX"), pas une liste vide silencieuse.
export async function fetchOne<T>(
  url: string, header: string, value: string,
): Promise<{ found: boolean; data?: T; message?: string }> {
  try {
    const response = await axios.get(url, {
      headers: { [header]: Crypter.encrypt(value), 'Content-Type': 'application/json' },
      withCredentials: true,
    });
    const res = JSON.parse(Crypter.decrypt(response.data));
    if (res?.StatutCode === 200) return { found: true, data: res?.Result?.Table?.[0] };
    return { found: false, message: res?.Message };
  } catch (error: any) {
    try {
      const res = JSON.parse(Crypter.decrypt(error.response.data));
      return { found: false, message: res?.Message };
    } catch {
      return { found: false, message: undefined };
    }
  }
}
